"use client"

import { DialogTitle } from "@radix-ui/react-dialog"
import Image from "next/image"
import { type ComponentProps, useEffect, useState } from "react"
import type { CarouselApi } from "@/components/ui/carousel"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn, getImageWithFallback } from "@/utilities"
import type { ReviewMedia } from "./types"

interface ReviewMediaCarouselProps extends ComponentProps<"div"> {
  media: ReviewMedia[]
  reviewAuthor?: string
}

export function ReviewMediaCarousel({
  media,
  reviewAuthor = "Cliente",
  className,
  ...props
}: ReviewMediaCarouselProps) {
  const [modalApi, setModalApi] = useState<CarouselApi | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)

  /* Sync modal carousel when opened */
  useEffect(() => {
    if (isModalOpen && modalApi) {
      modalApi.scrollTo(currentIndex, true)
    }
  }, [isModalOpen, modalApi, currentIndex])

  if (media.length === 0) {
    return null
  }

  const currentMedia = media[currentIndex]

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const isEnterKey = event.key === "Enter"
    const isSpaceKey = event.key === " "

    if (isEnterKey || isSpaceKey) {
      event.preventDefault()
      handleOpenModal()
    }
  }

  return (
    <>
      <div
        className={cn("flex w-full max-w-25 flex-col gap-3", className)}
        {...props}
      >
        <button
          className='group relative grid aspect-square w-full overflow-hidden rounded-lg bg-muted'
          onClick={handleOpenModal}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          type='button'
        >
          {currentMedia.type === "image" ? (
            <Image
              alt={`Mídia de avaliação de ${reviewAuthor}`}
              className='object-cover transition-transform duration-300 hover:scale-105'
              fill
              sizes='(max-width: 768px) 100vw, 400px'
              src={getImageWithFallback(currentMedia.url)}
              unoptimized={currentMedia.url.includes("localhost")}
            />
          ) : (
            <video
              className='size-full object-cover'
              controls
              src={currentMedia.url}
            >
              <track kind='captions' />
            </video>
          )}
          <div
            aria-label='Instruções para visualizar mídia ampliada'
            className='z-50 mb-4 self-end rounded-lg bg-black/25 px-4 py-2 backdrop-blur-sm'
            role='tooltip'
          />
        </button>

        {media.length > 1 && (
          <Carousel
            className='group mx-auto w-full'
            opts={{
              align: "start",
              loop: false,
            }}
            orientation='horizontal'
          >
            <CarouselContent className='w-full gap-2'>
              {media.map((item, index) => {
                const isSelected = index === currentIndex
                const borderColor = isSelected
                  ? "border-primary"
                  : "border-border hover:border-primary/30"

                return (
                  <CarouselItem
                    className={cn("basis-1/4 border p-0", borderColor)}
                    key={item.id}
                  >
                    <button
                      className='relative aspect-square w-full overflow-hidden rounded transition-colors'
                      onClick={() => setCurrentIndex(index)}
                      type='button'
                    >
                      {item.type === "image" ? (
                        <Image
                          alt={`${reviewAuthor} - mídia ${index + 1}`}
                          className='object-cover'
                          fill
                          sizes='80px'
                          src={getImageWithFallback(item.url)}
                          unoptimized={item.url.includes("localhost")}
                        />
                      ) : (
                        <video
                          className='size-full object-cover'
                          src={item.url}
                        >
                          <track kind='captions' />
                        </video>
                      )}
                    </button>
                  </CarouselItem>
                )
              })}
            </CarouselContent>

            <CarouselPrevious
              className='opacity-0 transition-opacity duration-300 group-hover:opacity-100'
              internal
            />
            <CarouselNext
              className='opacity-0 transition-opacity duration-300 group-hover:opacity-100'
              internal
            />
          </Carousel>
        )}
      </div>

      <Dialog onOpenChange={setIsModalOpen} open={isModalOpen}>
        <DialogContent className='max-w-4xl border-0 bg-black/95 p-0'>
          <DialogTitle className='sr-only'>
            Mídia de avaliação de {reviewAuthor}
          </DialogTitle>

          <Carousel
            className='w-full'
            opts={{ loop: true }}
            setApi={setModalApi}
          >
            <CarouselContent aria-describedby='Mídia de avaliação'>
              {media.map((item) => (
                <CarouselItem key={item.id}>
                  <div className='relative aspect-square bg-black'>
                    {item.type === "image" ? (
                      <Image
                        alt={`Mídia de avaliação de ${reviewAuthor}`}
                        className='object-contain'
                        fill
                        sizes='(max-width: 768px) 100vw, 768px'
                        src={getImageWithFallback(item.url)}
                        unoptimized={item.url.includes("localhost")}
                      />
                    ) : (
                      <video
                        className='size-full object-contain'
                        controls
                        src={item.url}
                      >
                        <track kind='captions' />
                      </video>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious className='p-4 text-primary disabled:text-muted-foreground' />
            <CarouselNext className='p-4 text-primary disabled:text-muted-foreground' />
          </Carousel>
        </DialogContent>
      </Dialog>
    </>
  )
}
